import { useCallback, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { getRealEstateEvents } from '../api';
import { IRealEstateEvent } from '../interfaces';

function useLoadRealEstateEvents(realEstateId: string, onlyActive: boolean) {
  const { enqueueSnackbar } = useSnackbar();

  const [realEstateEvents, setRealEstateEvents] = useState<IRealEstateEvent[]>([]);
  const [state, setState] = useState<{ loading: boolean; error: string | null }>({ loading: false, error: null });

  const fetchRealEstateEvents = useCallback(async () => {
    if (!realEstateId) return;

    try {
      setState({ loading: true, error: null });

      const realEstateEvents = await getRealEstateEvents(realEstateId, onlyActive);

      setRealEstateEvents(
        realEstateEvents.map(
          ({ realEstateEventTypeId, realEstateEventTypeName, eventEndTime, eventStartTime, ...realEstateEvent }) => ({
            ...realEstateEvent,
            comment: '',
            eventStartTime: eventEndTime ? format(new Date(eventStartTime), 'yyyy-MM-dd HH:mm') : '',
            eventEndTime: eventEndTime ? format(new Date(eventEndTime), 'yyyy-MM-dd HH:mm') : '',
            realEstateEventTypeId,
            realEstateEventTypeName,
            realEstateEventType: { realEstateEventTypeId, realEstateEventTypeName },
          }),
        ),
      );
    } catch (error) {
      const message = (error as AxiosError).response?.data.message;

      setState({ loading: false, error: message });
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setState((prevState) => ({ ...prevState, loading: false }));
    }
  }, [realEstateId, onlyActive]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchRealEstateEvents();
  }, [onlyActive]); // eslint-disable-line react-hooks/exhaustive-deps

  return { realEstateEvents, loadEventsState: state, fetchRealEstateEvents };
}

export default useLoadRealEstateEvents;
